import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { AxiosError } from 'axios';
import { v4 as uuid } from 'uuid';
import { AdminPageWrapper } from 'components/layouts/AdminPageWrapper';
import { api } from 'constants/api';
import { ApiError, ApiResponse } from 'types/utils/api-response';
import { PurchaseHistory } from 'hooks/useGetPaymentHistory';
import { purchaseHistoryColumns } from 'screens/PurchaseCreditsScreen/components/PurchaseHistoryTab';

export type BusinessCredits = {
  id: number;
  name: string;
  total: number;
  spent: number;
  scheduled: number;
  remaining: number;
};
const formatCredits = (value: number) => value.toLocaleString('en-GB');

export const CreditAdjustmentModal = ({
  business,
  onClose,
  onSaved,
}: {
  business: BusinessCredits;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [form] = Form.useForm();
  const direction = Form.useWatch('direction', form) || 'add';
  const amount = Form.useWatch('amount', form) || 0;
  const [submitted, setSubmitted] = useState(false);
  const requestKey = useRef(uuid());
  const adjustment = useMutation({
    mutationFn: async (values: {
      direction: string;
      amount: number;
      reason: string;
    }) => {
      const { data } = await api.post(
        `/business/${business.id}/credits/adjustments`,
        {
          amount:
            values.direction === 'remove' ? -values.amount : values.amount,
          reason: values.reason.trim(),
          adjustment_key: requestKey.current,
        }
      );
      return data;
    },
    onSuccess: onSaved,
    onError: (error: AxiosError<ApiError>) => {
      // Keep the reference and form fixed after an ambiguous network failure.
      // Retrying then retrieves the original audit instead of applying it twice.
      if (error.response && error.response.status < 500) {
        setSubmitted(false);
        requestKey.current = uuid();
      }
    },
    retry: false,
  });
  const delta = direction === 'remove' ? -amount : amount;
  const insufficient = direction === 'remove' && amount > business.remaining;
  return (
    <Modal
      open
      title={`Adjust credits — ${business.name}`}
      onCancel={onClose}
      closable={!adjustment.isPending}
      maskClosable={false}
      footer={null}
    >
      <Typography.Paragraph>
        Available balance: <strong>{formatCredits(business.remaining)}</strong>
      </Typography.Paragraph>
      <Form
        form={form}
        layout='vertical'
        initialValues={{ direction: 'add' }}
        onFinish={(values) => {
          setSubmitted(true);
          adjustment.mutate(values);
        }}
      >
        <Form.Item name='direction' label='Adjustment'>
          <Radio.Group
            disabled={submitted}
            options={[
              { label: 'Add credits', value: 'add' },
              { label: 'Remove credits', value: 'remove' },
            ]}
          />
        </Form.Item>
        <Form.Item
          name='amount'
          label='Number of credits'
          rules={[
            { required: true, message: 'Enter the number of credits.' },
            {
              validator: (_, value) =>
                Number.isInteger(value) && value > 0 && value <= 2147483647
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error('Enter a positive whole number of credits.')
                    ),
            },
          ]}
        >
          <InputNumber
            disabled={submitted}
            min={1}
            max={2147483647}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name='reason'
          label='Reason'
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Enter a reason for this adjustment.',
            },
            { max: 500 },
          ]}
        >
          <Input.TextArea
            disabled={submitted}
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>
        <Typography.Paragraph>
          New total allocation:{' '}
          <strong>{formatCredits(business.total + delta)}</strong>
          <br />
          New available balance:{' '}
          <strong>{formatCredits(business.remaining + delta)}</strong>
        </Typography.Paragraph>
        <Typography.Paragraph type='secondary'>
          This adjustment will be recorded in Purchase History with your name,
          the date and the reason.
        </Typography.Paragraph>
        {insufficient && (
          <Alert
            type='error'
            showIcon
            message='You cannot remove more credits than the available balance.'
            style={{ marginBottom: 16 }}
          />
        )}
        {adjustment.isError && (
          <Alert
            type='error'
            showIcon
            style={{ marginBottom: 16 }}
            message={
              adjustment.error.response?.data?.message ||
              'The result could not be confirmed. Retry this adjustment to check it safely.'
            }
          />
        )}
        <Space>
          <Button onClick={onClose} disabled={adjustment.isPending}>
            Cancel
          </Button>
          <Button
            type='primary'
            htmlType='submit'
            danger={direction === 'remove'}
            loading={adjustment.isPending}
            disabled={insufficient}
          >
            {submitted && adjustment.isError
              ? 'Retry adjustment'
              : direction === 'remove'
                ? 'Remove credits'
                : 'Add credits'}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

const BusinessHistory = ({
  business,
  onClose,
}: {
  business: BusinessCredits;
  onClose: () => void;
}) => {
  const history = useQuery({
    queryKey: ['business/credits/history', business.id],
    queryFn: async () =>
      (
        await api.get<ApiResponse<PurchaseHistory[]>>(
          `/business/${business.id}/credits/history`
        )
      ).data.data,
  });
  return (
    <Modal
      open
      title={`Purchase History — ${business.name}`}
      width={1100}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {history.isError ? (
        <Alert
          type='error'
          message='Unable to load purchase history.'
          action={<Button onClick={() => history.refetch()}>Retry</Button>}
        />
      ) : (
        <Table
          rowKey='id'
          columns={purchaseHistoryColumns(true)}
          dataSource={history.data || []}
          loading={history.isFetching}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 850 }}
        />
      )}
    </Modal>
  );
};

export const AdminBusinessCredits = () => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<BusinessCredits | null>(null);
  const [historyBusiness, setHistoryBusiness] =
    useState<BusinessCredits | null>(null);
  const [messageApi, messageContext] = message.useMessage();
  const queryClient = useQueryClient();
  const businesses = useQuery({
    queryKey: ['business/credits'],
    queryFn: async () =>
      (await api.get<ApiResponse<BusinessCredits[]>>('/business/credits')).data
        .data,
  });
  const rows = businesses.data || [];
  const columns: ColumnsType<BusinessCredits> = [
    {
      title: 'Business',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
    },
    ...(
      [
        { key: 'total', label: 'Total credits' },
        { key: 'spent', label: 'Used' },
        { key: 'scheduled', label: 'Reserved' },
        { key: 'remaining', label: 'Available balance' },
      ] as const
    ).map(({ key, label }) => ({
      title: label,
      dataIndex: key,
      key,
      align: 'right' as const,
      sorter: (a: BusinessCredits, b: BusinessCredits) => a[key] - b[key],
      render: formatCredits,
    })),
    {
      title: 'Actions',
      key: 'actions',
      render: (_, business) => (
        <Space>
          <Button onClick={() => setSelected(business)}>Adjust credits</Button>
          <Button type='link' onClick={() => setHistoryBusiness(business)}>
            History
          </Button>
        </Space>
      ),
    },
  ];
  return (
    <AdminPageWrapper>
      {messageContext}
      <Typography.Title level={2}>Business Credits</Typography.Title>
      <Typography.Paragraph type='secondary'>
        Total credits include purchases and manual adjustments. Used credits
        include test messages. Reserved credits are held for scheduled
        campaigns.
      </Typography.Paragraph>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {(
          [
            { key: 'total', label: 'Total credits' },
            { key: 'spent', label: 'Used credits' },
            { key: 'scheduled', label: 'Reserved credits' },
            { key: 'remaining', label: 'Available balance' },
          ] as const
        ).map(({ key, label }) => (
          <Col xs={24} sm={12} xl={6} key={key}>
            <Card>
              <Statistic
                title={label}
                value={rows.reduce((sum, row) => sum + row[key], 0)}
                loading={businesses.isLoading}
              />
            </Card>
          </Col>
        ))}
      </Row>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          aria-label='Search businesses'
          placeholder='Search businesses'
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Button
          loading={businesses.isFetching}
          onClick={() => businesses.refetch()}
        >
          Refresh
        </Button>
        <Typography.Text type='secondary'>
          {rows.length} businesses
        </Typography.Text>
      </Space>
      {businesses.isError ? (
        <Alert
          type='error'
          showIcon
          message='Unable to load business credits. Please try Refresh.'
        />
      ) : (
        <Table
          rowKey='id'
          columns={columns}
          dataSource={rows.filter((b) =>
            b.name.toLowerCase().includes(search.toLowerCase())
          )}
          loading={businesses.isFetching}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1000 }}
        />
      )}
      {selected && (
        <CreditAdjustmentModal
          business={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            messageApi.success(
              'Credit adjustment saved and added to Purchase History.'
            );
            queryClient.invalidateQueries({ queryKey: ['business/credits'] });
            queryClient.invalidateQueries({
              queryKey: ['business/credits/history'],
            });
            queryClient.invalidateQueries({ queryKey: ['purchase/history'] });
            queryClient.invalidateQueries({
              queryKey: ['purchase/current-credits'],
            });
          }}
        />
      )}
      {historyBusiness && (
        <BusinessHistory
          business={historyBusiness}
          onClose={() => setHistoryBusiness(null)}
        />
      )}
    </AdminPageWrapper>
  );
};
